<template>
  <VaSidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :minimized-width="minimizedSidebarWidth"
    :minimized="minimized"
    :color="color"
    :overlay="mobile && writableVisible"
    :close-on-click-outside="mobile"
    class="app-sidebar"
    :class="{
      'app-sidebar--mobile': mobile,
      'app-sidebar--minimized': minimized,
    }"
  >
    <div class="sidebar-content">
      <!-- Main Navigation -->
      <div class="sidebar-main-nav">
        <VaAccordion v-model="value" multiple>
          <VaCollapse v-for="(route, index) in filteredNavigationRoutes" :key="index">
            <template #header="{ value: isCollapsed }">
              <VaSidebarItem
                :to="route.children ? undefined : { name: route.name }"
                :active="routeHasActiveChild(route)"
                :active-color="activeColor"
                :text-color="textColor(route)"
                :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
                role="button"
                hover-opacity="0.10"
              >
                <VaSidebarItemContent :class="minimizedContentClass">
                  <VaIcon
                    v-if="route.meta.icon"
                    aria-hidden="true"
                    :name="route.meta.icon"
                    size="20px"
                    :color="iconColor(route)"
                    :class="minimizedIconClass"
                  />
                  <VaSidebarItemTitle
                    v-if="!minimized"
                    class="flex justify-between items-center leading-5 font-semibold"
                  >
                    {{ t(route.displayName) }}
                    <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </template>
            <template #body>
              <div v-for="(childRoute, index2) in route.children" :key="index2">
                <VaSidebarItem
                  :to="{ name: childRoute.name }"
                  :active="isActiveChildRoute(childRoute)"
                  :active-color="activeColor"
                  :text-color="textColor(childRoute)"
                  :aria-label="`Visit ${t(route.displayName)}`"
                  hover-opacity="0.10"
                >
                  <VaSidebarItemContent class="py-3 pr-2 pl-11">
                    <VaSidebarItemTitle class="leading-5 font-semibold">
                      {{ t(childRoute.displayName) }}
                    </VaSidebarItemTitle>
                  </VaSidebarItemContent>
                </VaSidebarItem>
              </div>
            </template>
          </VaCollapse>
        </VaAccordion>
      </div>

      <!-- Bottom Navigation & User Menu -->
      <div class="sidebar-bottom">
        <!-- Profile and Settings Navigation -->
        <!-- <div class="sidebar-bottom-nav">
          <VaSidebarItem
            v-for="(route, index) in filteredBottomRoutes"
            :key="index"
            :to="{ name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`Visit ${t(route.displayName)}`"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent :class="minimizedContentClass">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
                :class="minimizedIconClass"
              />
              <VaSidebarItemTitle v-if="!minimized" class="leading-5 font-semibold">
                {{ t(route.displayName) }}
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </div> -->

        <!-- User Menu -->
        <div class="sidebar-user-menu">
          <ProfileDropdown :options="sidebarProfileOptions">
            <VaSidebarItem
              class="user-menu-item"
              :class="{ 'user-menu-item--minimized': minimized }"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent :class="minimizedUserMenuContentClass">
                <UserAvatar
                  :size="minimized ? 20 : 32"
                  color="primary"
                  :font-size="minimized ? '0.75em' : '1em'"
                  :class="minimizedIconClass"
                  class="font-semibold"
                />
                <VaSidebarItemTitle v-if="!minimized" class="leading-5">
                  <div class="user-info">
                    <div class="user-name">Welcome {{ authStore.memberFirstName }}!</div>
                  </div>
                </VaSidebarItemTitle>
                <VaIcon v-if="!minimized" name="expand_less" size="16px" color="secondary" />
              </VaSidebarItemContent>
            </VaSidebarItem>
          </ProfileDropdown>
        </div>
      </div>
    </div>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import UserAvatar from '../UserAvatar.vue'
import ProfileDropdown from '../navbar/components/dropdowns/ProfileDropdown.vue'
import { useAuthStore } from '../../stores/auth'
import { isApproved } from '../../utils/approvalUtils'

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  action?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

export default defineComponent({
  name: 'Sidebar',
  components: {
    UserAvatar,
    ProfileDropdown,
  },
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    minimized: { type: Boolean, default: false },
    animated: { type: Boolean, default: true },
  },
  emits: ['update:visible', 'toggleMinimize'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba, setHSLAColor, colors } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const authStore = useAuthStore()

    const value = ref<boolean[]>([])

    // Custom profile options for sidebar - different from navbar
    const sidebarProfileOptions = computed(() => {
      const userApprovalStatus = authStore.memberDetails?.approvalStatus
      const approved = isApproved(userApprovalStatus)

      const baseOptions: ProfileOptions[] = [
        {
          name: '',
          separator: true,
          list: [
            {
              name: 'profile',
              to: 'profile',
              icon: 'mso-account_circle',
            },
            {
              name: 'settings',
              to: 'settings',
              icon: 'mso-settings',
            },
          ],
        },
        {
          name: '',
          separator: true,
          list: [
            {
              name: 'helpAndSupport',
              href: 'mailto:members@holycross.ke',
              icon: 'mso-help',
            },
          ],
        },
        {
          name: '',
          separator: false,
          list: [
            {
              name: 'logout',
              action: 'logout',
              icon: 'mso-logout',
            },
          ],
        },
      ]

      // If user is not approved, filter out profile and settings
      if (!approved) {
        return baseOptions
          .map((group) => ({
            ...group,
            list: group.list.filter((item) => item.name !== 'profile' && item.name !== 'settings'),
          }))
          .filter((group) => group.list.length > 0) // Remove empty groups
      }

      return baseOptions
    })

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    // Role-based navigation filtering
    const filteredNavigationRoutes = computed(() => {
      const userRole = authStore.userRole

      return navigationRoutes.routes.filter((route) => {
        // If no allowedRoles specified, show to all
        if (!route.allowedRoles) return true
        // If user has no role, show nothing
        if (!userRole) return false
        // Check if user's role is in allowed roles
        return route.allowedRoles.includes(userRole)
      })
    })

    const filteredBottomRoutes = computed(() => {
      const userRole = authStore.userRole

      return navigationRoutes.bottomRoutes.filter((route) => {
        // If no allowedRoles specified, show to all
        if (!route.allowedRoles) return true
        // If user has no role, show nothing
        if (!userRole) return false
        // Check if user's role is in allowed roles
        return route.allowedRoles.includes(userRole)
      })
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    const setActiveExpand = () =>
      (value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => {
      if (props.mobile) {
        return '50vw'
      }
      return '280px'
    })

    const minimizedSidebarWidth = computed(() => {
      if (props.mobile) {
        return '0'
      }
      return 'calc(20px + 2em)'
    })
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))
    const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    const minimizedContentClass = computed(() => ({
      'py-3 px-3': !props.minimized,
      'py-3 px-3 justify-center': props.minimized,
    }))

    const minimizedUserMenuContentClass = computed(() => ({
      'py-3 pr-2 pl-3': !props.minimized,
      'py-3 px-3 justify-center': props.minimized,
    }))

    const minimizedIconClass = computed(() => ({
      'mr-3': !props.minimized,
      'mr-0': props.minimized,
    }))

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    const toggleSidebar = () => {
      if (props.mobile) {
        // On mobile, toggle visibility which triggers the parent's minimize logic
        emit('toggleMinimize')
      } else {
        // On desktop, just toggle minimize state
        emit('toggleMinimize')
      }
    }

    return {
      writableVisible,
      sidebarProfileOptions,
      sidebarWidth,
      minimizedSidebarWidth,
      value,
      color,
      activeColor,
      hoverColor,
      navigationRoutes,
      filteredNavigationRoutes,
      filteredBottomRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      minimizedContentClass,
      minimizedUserMenuContentClass,
      minimizedIconClass,
      authStore,
      t,
      iconColor,
      textColor,
      arrowDirection,
      toggleSidebar,
    }
  },
})
</script>

<style lang="scss" scoped>
.app-sidebar {
  transition: all 0.3s ease;

  &--minimized {
    :deep(.va-sidebar__content) {
      overflow: visible;
    }

    @media (max-width: 640px) {
      display: none;
    }
  }

  // Responsive breakpoints
  @media (max-width: 768px) {
    &:not(.app-sidebar--minimized) {
      width: 100vw !important;
      max-width: 100vw;
    }
  }

  @media (max-width: 640px) {
    &:not(.app-sidebar--minimized) {
      width: 100vw !important;
      max-width: 100vw;
    }
  }
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  height: 100%;

  // @media (max-width: 768px) {
  //   min-height: 100vh;
  // }
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid var(--va-background-border);
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
}

.sidebar-logo {
  flex: 1;

  a {
    text-decoration: none;
    color: inherit;
  }
}

.sidebar-toggle {
  padding: 8px !important;
  min-width: auto !important;
  transition: all 0.3s ease;

  &--minimized {
    margin: 0 auto;
  }

  :deep(.va-button__content) {
    padding: 0;
  }

  &:hover {
    background-color: var(--va-background-element);
  }

  // Make toggle more prominent on mobile
  @media (max-width: 768px) {
    padding: 12px !important;

    :deep(.va-icon) {
      font-size: 1.5rem;
    }
  }
}

.sidebar-main-nav {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 1rem;

  // Smooth scrolling on mobile
  @media (max-width: 768px) {
    -webkit-overflow-scrolling: touch;
  }
}

.sidebar-bottom {
  margin-top: auto;
  border-top: 1px solid var(--va-background-border);
  flex-shrink: 0;
}

.sidebar-bottom-nav {
  margin-bottom: 1rem;
}

.user-menu-item {
  // Better touch targets on mobile
  @media (max-width: 768px) {
    min-height: 48px;

    :deep(.va-sidebar-item__content) {
      min-height: 48px;
    }
  }
}

.user-info {
  flex: 1;
  text-align: left;
  min-width: 0;
}

.user-name {
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1rem;
  color: var(--va-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--va-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-menu-dropdown-content {
  .menu-item:hover {
    background: var(--hover-color);
  }
}

.user-menu-dropdown {
  width: 100%;
}

// Improve touch targets on mobile devices
@media (max-width: 768px) {
  :deep(.va-sidebar-item) {
    min-height: 44px;

    .va-sidebar-item__content {
      min-height: 44px;
      padding: 12px 16px;
    }
  }

  :deep(.va-collapse__header) {
    min-height: 44px;
  }

  :deep(.va-sidebar-item__title) {
    font-size: 1rem;
    line-height: 1.5;
  }
}

// Improve minimized sidebar on desktop
@media (min-width: 769px) {
  .app-sidebar--minimized {
    :deep(.va-sidebar-item__content) {
      justify-content: center;
      padding: 12px 8px;
    }

    :deep(.va-icon) {
      margin-right: 0 !important;
    }
  }
}

:deep(.va-list-separator) {
  background-color: var(--va-background-border) !important;
}

:deep(.va-dropdown .va-list-separator) {
  background-color: var(--va-background-border) !important;
}

:deep(.va-dropdown-content .va-list-separator) {
  background-color: var(--va-background-border) !important;
}

:deep(.va-button__content) {
  padding: 0;
  width: 100%;
}

// Ensure proper z-index for overlay
:deep(.va-sidebar--overlay) {
  z-index: 1000;
}

// Smooth transitions
:deep(.va-sidebar) {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

// Hide scrollbar on minimized sidebar
.app-sidebar--minimized {
  :deep(.sidebar-main-nav) {
    overflow: hidden;

    &:hover {
      overflow-y: auto;
    }
  }
}
</style>
